import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Bell, Plus, Edit2, Trash2, Send, Clock, Users, CheckCircle, AlertTriangle, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const NotificationManagerPage = () => {
  const [notifications, setNotifications] = useState([
    { id: '1', title: 'License Expiration Reminder', type: 'Alert', recipients: 'All Staff', schedule: 'Daily', status: 'Active', lastSent: '01/26/2026 08:00 AM' },
    { id: '2', title: 'Visit Reminder', type: 'Reminder', recipients: 'Clinicians', schedule: '24 hours before', status: 'Active', lastSent: '01/26/2026 07:00 AM' },
    { id: '3', title: 'Overdue Documentation', type: 'Alert', recipients: 'Supervisors', schedule: 'Daily', status: 'Active', lastSent: '01/26/2026 06:00 AM' },
    { id: '4', title: 'OASIS Lock Reminder', type: 'Reminder', recipients: 'Clinical Staff', schedule: '5 days after visit', status: 'Active', lastSent: '01/25/2026 10:00 AM' },
    { id: '5', title: 'Certification Expiring', type: 'Alert', recipients: 'Case Managers', schedule: '7 days before', status: 'Active', lastSent: '01/24/2026 09:00 AM' },
    { id: '6', title: 'Weekly Staff Summary', type: 'Report', recipients: 'Administrators', schedule: 'Weekly (Monday)', status: 'Inactive', lastSent: '01/20/2026 08:00 AM' },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'Alert',
    recipients: '',
    schedule: '',
    message: '',
    emailEnabled: true,
    smsEnabled: false,
    inAppEnabled: true
  });

  const notificationTypes = ['Alert', 'Reminder', 'Report', 'Announcement'];
  const recipientGroups = ['All Staff', 'Clinicians', 'Supervisors', 'Administrators', 'Case Managers', 'Billing Staff', 'Clinical Staff'];

  const handleAdd = () => {
    setEditingNotification(null);
    setFormData({ title: '', type: 'Alert', recipients: '', schedule: '', message: '', emailEnabled: true, smsEnabled: false, inAppEnabled: true });
    setShowModal(true);
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    setFormData({ ...notification });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    setNotifications(prev => prev.filter(n => n.id !== id));
    toast.success('Notification deleted');
  };

  const handleToggleStatus = (notification) => {
    setNotifications(prev => prev.map(n => 
      n.id === notification.id ? { ...n, status: n.status === 'Active' ? 'Inactive' : 'Active' } : n
    ));
    toast.success(`Notification ${notification.status === 'Active' ? 'deactivated' : 'activated'}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingNotification) {
      setNotifications(prev => prev.map(n => n.id === editingNotification.id ? { ...n, ...formData } : n));
      toast.success('Notification updated');
    } else {
      setNotifications(prev => [...prev, { ...formData, id: Date.now().toString(), status: 'Active', lastSent: 'Never' }]);
      toast.success('Notification created');
    }
    setShowModal(false);
  };

  const handleSendNow = (notification) => {
    toast.success(`Sending "${notification.title}" to ${notification.recipients}...`);
  };

  const getTypeColor = (type) => {
    const colors = {
      'Alert': 'bg-red-100 text-red-800',
      'Reminder': 'bg-blue-100 text-blue-800',
      'Report': 'bg-green-100 text-green-800',
      'Announcement': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notification-manager-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#f39c12] rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notification Manager</h1>
              <p className="text-gray-600">Configure and manage automated notifications</p>
            </div>
          </div>
          <Button onClick={handleAdd} className="bg-[#f39c12] hover:bg-[#e67e22]" data-testid="add-notification-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Notification
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{notifications.filter(n => n.status === 'Active').length}</p>
                <p className="text-sm text-gray-500">Active Notifications</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{notifications.filter(n => n.type === 'Alert').length}</p>
                <p className="text-sm text-gray-500">Alerts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{notifications.filter(n => n.type === 'Reminder').length}</p>
                <p className="text-sm text-gray-500">Reminders</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{notifications.length}</p>
                <p className="text-sm text-gray-500">Total Notifications</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Notification</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Recipients</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Schedule</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Sent</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{notification.title}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(notification.type)}`}>
                        {notification.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{notification.recipients}</td>
                    <td className="px-4 py-3 text-gray-600">{notification.schedule}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{notification.lastSent}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleStatus(notification)}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          notification.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {notification.status}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleSendNow(notification)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Send Now">
                          <Send className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEdit(notification)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(notification.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold">{editingNotification ? 'Edit Notification' : 'Create Notification'}</h3>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <Label>Notification Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type *</Label>
                    <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                      {notificationTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Recipients *</Label>
                    <select value={formData.recipients} onChange={(e) => setFormData({...formData, recipients: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Select...</option>
                      {recipientGroups.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Schedule</Label>
                  <Input value={formData.schedule} onChange={(e) => setFormData({...formData, schedule: e.target.value})} placeholder="e.g., Daily, Weekly, 7 days before" />
                </div>
                <div>
                  <Label>Message Template</Label>
                  <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="w-full px-3 py-2 border rounded-lg resize-none" rows={3} placeholder="Enter notification message..." />
                </div>
                <div>
                  <Label className="mb-2 block">Delivery Methods</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.emailEnabled} onChange={(e) => setFormData({...formData, emailEnabled: e.target.checked})} />
                      <Mail className="w-4 h-4" /> Email
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.smsEnabled} onChange={(e) => setFormData({...formData, smsEnabled: e.target.checked})} />
                      <MessageSquare className="w-4 h-4" /> SMS
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.inAppEnabled} onChange={(e) => setFormData({...formData, inAppEnabled: e.target.checked})} />
                      <Bell className="w-4 h-4" /> In-App
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#f39c12] hover:bg-[#e67e22]">Save Notification</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationManagerPage;
